// src/components/pos/DialogActivarPOS.js
//
// D-23 — Cómo empezar con HolaVeci Local desde la landing.
//
//   · "Ya tengo HolaVeci Negocio" → instructivo: HolaVeci Local se agrega
//     desde la app móvil (Perfil → Agregar HolaVeci Local), se paga un solo
//     plan (HolaVeci 360°) y luego se activa en local.holaveci.co.
//   · "Solo quiero HolaVeci Local" → registro propio en
//     local.holaveci.co/registro (documentos, validación y pago del plan).
//   · enfoque="360" (botón del paquete completo) → instructivo para quien
//     aún no tiene nada: registrarse en la app HolaVeci Negocio y desde
//     ahí agregar Local.
//
// Ya no existe registro de negocio desde esta web: el de HolaVeci Negocio
// es en la app (código de guía, GPS, documentos) y el de solo Local es
// en local.holaveci.co.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Stack, Box, Divider,
} from '@mui/material';
import PointOfSaleIcon    from '@mui/icons-material/PointOfSale';
import StorefrontIcon     from '@mui/icons-material/Storefront';
import PersonAddIcon      from '@mui/icons-material/PersonAdd';
import ArrowForwardIcon   from '@mui/icons-material/ArrowForward';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import OpenInNewIcon      from '@mui/icons-material/OpenInNew';
import PhoneIphoneIcon    from '@mui/icons-material/PhoneIphone';

export const URL_HOLAVECI_LOCAL = process.env.REACT_APP_LOCAL_URL || 'https://local.holaveci.co';

function Opcion({ icono, color, titulo, texto, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2.5, border: '1.5px solid', borderColor: 'divider', borderRadius: 2,
        cursor: 'pointer', transition: 'all 0.15s',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(15,76,58,0.04)' },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, bgcolor: color, color: '#fff',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          {icono}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>{titulo}</Typography>
          <Typography variant="body2" color="text.secondary">{texto}</Typography>
        </Box>
        <ArrowForwardIcon color="action" />
      </Stack>
    </Box>
  );
}

function Pasos({ pasos }) {
  return (
    <Stack spacing={1.75}>
      {pasos.map((p, i) => (
        <Stack key={i} direction="row" spacing={1.75} alignItems="flex-start">
          <Box sx={{
            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
            bgcolor: 'secondary.main', color: 'primary.dark',
            display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 14,
          }}>
            {i + 1}
          </Box>
          <Box>
            <Typography fontWeight={700}>{p.titulo}</Typography>
            <Typography variant="body2" color="text.secondary">{p.texto}</Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

const PASOS_TIENE_NEGOCIO = [
  { titulo: 'Abre tu app HolaVeci Negocio',
    texto: 'Entra con tu celular y contraseña de siempre y ve a la pestaña Perfil.' },
  { titulo: 'Toca "Agregar HolaVeci Local"',
    texto: 'Pasas a HolaVeci 360°: hoy pagas solo la diferencia por los días que faltan del mes y desde el próximo mes un único plan por la app y el POS.' },
  { titulo: 'Activa HolaVeci Local en el computador',
    texto: 'Entra a local.holaveci.co con el mismo celular y contraseña, completa los datos fiscales de tu empresa y listo.' },
];

const PASOS_360_NUEVO = [
  { titulo: 'Descarga la app HolaVeci Negocio',
    texto: 'Regístrate con el código del guía que te acompaña y sube tus documentos.' },
  { titulo: 'Activa tu negocio',
    texto: 'Cuando validemos tus documentos, paga tu primer mes de HolaVeci Negocio desde la app.' },
  { titulo: 'Agrega HolaVeci Local',
    texto: 'En la app ve a Perfil → "Agregar HolaVeci Local" y activa el POS en local.holaveci.co con el mismo celular y contraseña.' },
];

const DialogActivarPOS = ({ abierto, onCerrar, enfoque = 'local', onDescargarApp }) => {
  // opciones | tieneNegocio | combo360
  const [vista, setVista] = useState('opciones');

  useEffect(() => {
    if (abierto) setVista(enfoque === '360' ? 'combo360' : 'opciones');
  }, [abierto, enfoque]);

  const irA = (ruta) => window.open(`${URL_HOLAVECI_LOCAL}${ruta}`, '_blank', 'noopener');

  const titulo = vista === 'combo360' ? 'Quiero HolaVeci 360°' : 'Activar HolaVeci Local';
  const subtitulo = vista === 'tieneNegocio'
    ? 'Ya tienes HolaVeci Negocio'
    : vista === 'combo360'
      ? 'HolaVeci Negocio + HolaVeci Local en un solo plan'
      : 'Elige tu caso';

  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 1.5, bgcolor: 'primary.dark', color: '#fff',
            display: 'grid', placeItems: 'center',
          }}>
            <PointOfSaleIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontFamily: 'Fraunces', lineHeight: 1.1 }}>{titulo}</Typography>
            <Typography variant="caption" color="text.secondary">{subtitulo}</Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {vista === 'opciones' && (
          <>
            <Stack spacing={2.5}>
              <Opcion
                icono={<StorefrontIcon fontSize="small" />}
                color="primary.main"
                titulo="Ya tengo HolaVeci Negocio"
                texto="Agrega HolaVeci Local desde tu app y paga un solo plan: HolaVeci 360°."
                onClick={() => setVista('tieneNegocio')}
              />
              <Opcion
                icono={<PersonAddIcon fontSize="small" />}
                color="primary.dark"
                titulo="Solo quiero HolaVeci Local"
                texto="Regístrate en HolaVeci Local: validamos tus documentos, pagas tu plan y activas tu caja e inventario."
                onClick={() => irA('/registro')}
              />
            </Stack>
            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              HolaVeci Negocio y HolaVeci Local son <b>módulos independientes que se complementan</b>.
              Puedes usar solo Local, solo Negocio, o los dos juntos con HolaVeci 360°.
            </Typography>
          </>
        )}

        {vista === 'tieneNegocio' && (
          <Stack spacing={2.5}>
            <Pasos pasos={PASOS_TIENE_NEGOCIO} />
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(15,76,58,0.06)' }}>
              <Typography variant="body2">
                No necesitas crear otra cuenta. Si ya tienes HolaVeci Local pagado, entra directamente
                a <b>local.holaveci.co</b>.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={() => irA('/login')}>
                Ir a HolaVeci Local
              </Button>
              {onDescargarApp && (
                <Button variant="outlined" startIcon={<PhoneIphoneIcon />} onClick={onDescargarApp}>
                  No tengo la app
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        {vista === 'combo360' && (
          <Stack spacing={2.5}>
            <Pasos pasos={PASOS_360_NUEVO} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {onDescargarApp && (
                <Button variant="contained" startIcon={<PhoneIphoneIcon />} onClick={onDescargarApp}>
                  Descargar la app
                </Button>
              )}
              <Button variant="outlined" onClick={() => setVista('tieneNegocio')}>
                Ya tengo HolaVeci Negocio
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: vista === 'opciones' ? 'flex-end' : 'space-between' }}>
        {vista !== 'opciones' && (
          <Button startIcon={<ArrowBackIcon />} onClick={() => setVista('opciones')}>
            Otras opciones
          </Button>
        )}
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogActivarPOS;
